/**
 * GetLabsSubTab.tsx
 *
 * Lists a patient's GetLabs (at-home draw) orders with their scheduling status.
 */
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import type { BadgeVariant } from '@/types/common.types';
import { getPatientIntegrationOrders } from '@/services/integrations.service';

const FULFILLMENT_LABELS: Record<string, string> = {
  pending_scheduling: 'Pending scheduling',
  scheduled: 'Scheduled',
  collected: 'Collected',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const BILLING_MODE_LABELS: Record<string, string> = {
  charge_patient: 'Charge patient',
  log_for_settlement: 'Log & settle monthly',
};

function fulfillmentVariant(status: string | null): BadgeVariant {
  switch (status) {
    case 'completed': return 'success';
    case 'collected':
    case 'scheduled': return 'info';
    case 'cancelled': return 'error';
    default: return 'warning'; // pending_scheduling
  }
}

interface GetLabsSubTabProps {
  patientId: number;
}

export function GetLabsSubTab({ patientId }: GetLabsSubTabProps) {
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['patient-getlabs-orders', patientId],
    queryFn: () => getPatientIntegrationOrders(patientId, 'getlabs'),
    staleTime: 30_000,
  });

  if (isLoading) {
    return <div className="flex justify-center py-10"><Spinner /></div>;
  }

  if (orders.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">
        No GetLabs visits yet. Use “Schedule GetLabs Visit” above to order an at-home draw.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Ordered</th>
            <th className="px-3 py-2">Product</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Billing</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.integrationOrderId} className="border-b border-slate-100">
              <td className="px-3 py-2 text-slate-600">
                {o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </td>
              <td className="px-3 py-2 text-slate-700">{o.productName ?? 'GetLabs draw'}</td>
              <td className="px-3 py-2">
                <Badge variant={fulfillmentVariant(o.fulfillmentStatus)}>
                  {FULFILLMENT_LABELS[o.fulfillmentStatus ?? ''] ?? (o.fulfillmentStatus ?? o.status)}
                </Badge>
              </td>
              <td className="px-3 py-2 text-slate-600">{BILLING_MODE_LABELS[o.billingMode] ?? o.billingMode}</td>
              <td className="px-3 py-2 text-right font-medium text-slate-900">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: o.currency || 'USD' }).format(o.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
